/* istanbul ignore file */
/**
 * Created by rathompson on 11/5/21
 * File: InheritedRoleModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useMemo, VFC } from 'react'
import {
  Bold,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tooltip,
  TruncateText,
  Body, ModalProps
} from '@panwds/react-ui'
import StringTemplate from '../../../../common/utils/StringTemplate'
import {
  LABEL_VIEW_INHERITED_TITLE,
  LABEL_NUM_PERMISSIONS,
  LABEL_NAME,
  LABEL_DESCRIPTION,
  LABEL_DESCRIPTIONS,
  BUTTON_CLOSE
} from '../../../constants/LabelConstants'
import { getVarValue } from '../../../../common/utils/Sparky'
import { FF_SHOW_PERMISSIONS } from '../../../../common/utils/FeatureFlags'
import type { IAM_Permission, IAM_Role } from '../../../../common/reducers/types/IAMReducer.types'

const showPermissions = getVarValue(FF_SHOW_PERMISSIONS)

/**
 *   Role details modal
 */
const InheritedRoleModal: VFC<ModalProps & {
  closeModal: () => void,
  role: IAM_Role,
  permissionsMap: { [id: string]: IAM_Permission }
}> = ({ closeModal, isOpen, role, permissionsMap }) => {
  /**
   *     ========= Local State ===========
   */
  const title = StringTemplate(LABEL_VIEW_INHERITED_TITLE, [role?.label])

  /**
   *     ========= Callback ===========
   */
  const permissions = useMemo(() => {
    return role?.permissions?.map((permission, index) => (
      <div key={index} className='tw-flex'>
        <TruncateText addClassName='tw-flex-1'>{permission}</TruncateText>
        <Tooltip label={permissionsMap[permission]?.description}>
          <TruncateText addClassName='tw-flex-1'>{permissionsMap[permission]?.description}</TruncateText>
        </Tooltip>
      </div>
    ))
  }, [permissionsMap, role?.permissions])

  return (
    <Modal style={{ top: '20%' }} size='lg' onClose={closeModal} isOpen={isOpen}>
      <div className='header-wrapper'>
        <ModalHeader title={title} />
      </div>
      <ModalBody addClassName='tw-max-h-full tw-overflow-hidden'>
        <div className='tw-mt-2 tw-flex tw-flex-col'>
          <Bold>{LABEL_NAME}</Bold>
          <TruncateText>{role?.label || role?.name}</TruncateText>
        </div>
        <div className='tw-mt-2 tw-flex tw-flex-col'>
          <Bold>{LABEL_DESCRIPTION}</Bold>
          <Body size='lg'>{role?.description}</Body>
        </div>
        {showPermissions && (
          <>
            <div className='tw-flex tw-mt-2'>
              <Bold addClassName='tw-flex-1'>{StringTemplate(LABEL_NUM_PERMISSIONS, [role?.permissions?.length])}</Bold>
              <Bold addClassName='tw-flex-1'>{LABEL_DESCRIPTIONS}</Bold>
            </div>
            <div className='tw-overflow-y-scroll' style={{ maxHeight: '435px' }}>
              {permissions}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={closeModal}
          appearance="secondary"
        >{BUTTON_CLOSE}</Button>
      </ModalFooter>
    </Modal>
  )
}

InheritedRoleModal.propTypes = {}
export default InheritedRoleModal
